/**
 * useSwipeGesture — SSOT for swipe-to-dismiss / swipe-to-navigate.
 *
 * Why a hook
 * ----------
 * The redesign §5.5 specifies "1:1 swipe-to-dismiss with velocity
 * threshold" for sheet-partial modals AND a "swipeable on mobile" wizard
 * navigation contract (mentorship welcome overlay, §4 / line 964). Both
 * call sites previously hand-rolled their own Touch/Mouse handlers,
 * creating two divergent code paths with subtly different cancellation
 * semantics (Modal.tsx's drag-handle vs. TouchTooltip.tsx's
 * PointerEvent path). The "stuck mid-drag" failure mode — finger leaves
 * the element before pointerup fires — was reproducible on both.
 *
 * This hook unifies them under a single PointerEvent code path with
 * `setPointerCapture`, so pointerup is guaranteed to fire even if the
 * gesture exits the target. One hook, one set of tests, one SSOT
 * threshold (per the project's SOLID + DRY discipline).
 *
 * Why PointerEvent
 * ----------------
 * PointerEvent is W3C-standard and covered by every browser the project
 * targets (Safari 13+, Chrome, Firefox, Edge). One handler tree
 * subsumes touch + mouse + pen — eliminating the touch/mouse fork that
 * the legacy Modal handlers had. No new npm deps; zero polyfill cost.
 *
 * Threshold rationale
 * -------------------
 * 50px default = mobile-ergonomic minimum. Per Apple HIG and Material
 * Design swipe affordances, anything under ~44px is below the
 * minimum-tap-target equivalent and conflicts with scroll intent
 * detection; anything over ~80px feels sluggish for the wizard's
 * "next step" gesture. Distance-based (not velocity-based) for
 * predictability: testing matrices reproduce a pixel delta cleanly,
 * whereas timing-based velocity thresholds drift across CI machines.
 *
 * a11y contract (CRITICAL)
 * ------------------------
 * Swipe is an ENHANCEMENT, never the sole input path. Every call site
 * MUST retain its existing button + keyboard affordance:
 *   - Modal sheet-partial keeps the close (X) button + Escape key.
 *   - MentorshipWelcomeOverlay keeps Next/Back buttons.
 * The `disabled` prop lets the consumer opt-out under
 * `prefers-reduced-motion: reduce` per §5.5 (drops the slide to
 * opacity-only). The hook itself honours prefers-reduced-motion by
 * default — set `respectReducedMotion: false` to override (rare).
 *
 * Telemetry
 * ---------
 * Emits `swipe.detected` with `{ direction, deltaPx }` for FTUE funnel
 * analysis. Defensive — if no TelemetryProvider is mounted the
 * underlying hook returns a no-op, so call sites are safe to wire
 * unconditionally.
 *
 * SOLID
 * -----
 * - SRP: this hook owns the GESTURE only. It never touches focus,
 *   modal lifecycle, or routing — that stays with the consumer
 *   (Modal.tsx owns FocusTrap + Escape; the wizard owns step state).
 * - OCP: new directions / new thresholds extend via props; the
 *   detection algorithm is unchanged.
 * - exactOptionalPropertyTypes: all optional callbacks are
 *   declared `?:` and consumers can omit any direction safely.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';
import { useTelemetry } from '../telemetry/useTelemetry';
import { captureError } from '../errors/captureError';

/** Discriminator for the emitted swipe direction. The hook decides
 *  the dominant axis (whichever delta is larger) and emits a single
 *  direction per gesture — never both. */
export type SwipeDirection = 'left' | 'right' | 'up' | 'down';

export type UseSwipeGestureProps = {
    /** Fires when a left-swipe crosses the threshold. */
    onSwipeLeft?: () => void;
    /** Fires when a right-swipe crosses the threshold. */
    onSwipeRight?: () => void;
    /** Fires when an up-swipe crosses the threshold. */
    onSwipeUp?: () => void;
    /** Fires when a down-swipe crosses the threshold. */
    onSwipeDown?: () => void;
    /** Minimum pixel distance to register as a swipe. Default 50 —
     *  mobile-ergonomic floor; see "Threshold rationale" above. */
    threshold?: number;
    /** When true, all pointer wiring is skipped — the returned
     *  handlers become no-ops. Consumers gate this on reduced-motion,
     *  desktop breakpoints, or modal-closed states. Default false. */
    disabled?: boolean;
    /** When true (default), the hook auto-disables itself when the
     *  user has `prefers-reduced-motion: reduce` set. Set to false
     *  only when the consumer has already taken responsibility for
     *  the reduced-motion fallback path. */
    respectReducedMotion?: boolean;
};

export type SwipeHandlers = {
    onPointerDown: (e: ReactPointerEvent<HTMLElement>) => void;
    onPointerMove: (e: ReactPointerEvent<HTMLElement>) => void;
    onPointerUp: (e: ReactPointerEvent<HTMLElement>) => void;
    onPointerCancel: (e: ReactPointerEvent<HTMLElement>) => void;
};

export type UseSwipeGestureReturn = {
    /** Spread onto the target element. When `disabled` (or
     *  reduced-motion) is in effect, every handler is a no-op so the
     *  consumer can wire them unconditionally. */
    handlers: SwipeHandlers;
    /** True while a pointer is down and a gesture is being tracked.
     *  Exposed for CSS / visual affordance hooks (drag-handle
     *  highlight, etc.). */
    isSwiping: boolean;
};

/** Track the active pointer's identifier + origin so multi-touch and
 *  re-entrant pointerdowns don't corrupt the gesture state. We only
 *  follow ONE pointer per gesture — additional pointers are ignored
 *  until the current one releases. */
type ActivePointer = {
    id: number;
    startX: number;
    startY: number;
};

/** Detect reduced-motion preference. SSR-safe (returns false when
 *  window/matchMedia are unavailable). */
function prefersReducedMotion(): boolean {
    if (typeof window === 'undefined') return false;
    if (typeof window.matchMedia !== 'function') return false;
    try {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch (err) {
        // matchMedia can throw on malformed queries in legacy jsdom —
        // be defensive (gesture detection must never break the product path).
        captureError(err, { scope: 'useSwipeGesture', op: 'prefersReducedMotion' });
        return false;
    }
}

/** No-op handler used in the disabled return shape. Stable identity
 *  (module-scoped) so consumers' useCallback deps don't churn. */
const NOOP_HANDLER = (): void => undefined;

const NOOP_HANDLERS: SwipeHandlers = {
    onPointerDown: NOOP_HANDLER,
    onPointerMove: NOOP_HANDLER,
    onPointerUp: NOOP_HANDLER,
    onPointerCancel: NOOP_HANDLER,
};

/**
 * Returns pointer handlers + isSwiping flag. Spread the handlers onto
 * the element that should accept the gesture.
 */
export function useSwipeGesture({
    onSwipeLeft,
    onSwipeRight,
    onSwipeUp,
    onSwipeDown,
    threshold = 50,
    disabled = false,
    respectReducedMotion = true,
}: UseSwipeGestureProps = {}): UseSwipeGestureReturn {
    const { track } = useTelemetry();
    const activeRef = useRef<ActivePointer | null>(null);
    const [isSwiping, setIsSwiping] = useState(false);

    // Subscribe to the reduced-motion query so the hook re-runs when
    // the user flips the OS setting mid-session (rare but correct).
    const [reducedMotion, setReducedMotion] = useState<boolean>(prefersReducedMotion);
    useEffect(() => {
        if (!respectReducedMotion) return;
        if (typeof window === 'undefined') return;
        if (typeof window.matchMedia !== 'function') return;
        let mql: MediaQueryList;
        try {
            mql = window.matchMedia('(prefers-reduced-motion: reduce)');
        } catch (err) {
            // Malformed-query path — defensive. Capture so we see the
            // breakage in telemetry; the gesture stays enabled because
            // we can't tell the reduced-motion state either way.
            captureError(err, { scope: 'useSwipeGesture', op: 'reducedMotion.subscribe' });
            return;
        }
        const handler = (e: MediaQueryListEvent) => setReducedMotion(e.matches);
        // Modern Safari 14+ / Chrome / Firefox / Edge expose
        // addEventListener('change', …) on MediaQueryList. The legacy
        // addListener/removeListener pair was removed from the modern
        // spec — we only reach the legacy fallback if the runtime
        // lacks the modern method, which today is effectively zero
        // (full evergreen coverage). The "fallback" branch is kept as
        // dead-code defence: if the runtime check passes, the value
        // is a MediaQueryList without addEventListener — which the
        // type system tracks as `never` at that point. No legacy
        // shim needed; the modern branch always wins.
        mql.addEventListener('change', handler);
        return () => mql.removeEventListener('change', handler);
    }, [respectReducedMotion]);

    const isInactive = disabled || (respectReducedMotion && reducedMotion);

    const onPointerDown = useCallback((e: ReactPointerEvent<HTMLElement>): void => {
        // Only one gesture at a time — ignore additional pointers.
        if (activeRef.current !== null) return;
        activeRef.current = {
            id: e.pointerId,
            startX: e.clientX,
            startY: e.clientY,
        };
        setIsSwiping(true);
        // setPointerCapture guarantees pointerup/pointercancel fire on
        // the originating element even if the gesture exits its bounds.
        // This solves the "stuck mid-drag" failure mode the legacy
        // touch handlers suffered.
        try {
            e.currentTarget.setPointerCapture(e.pointerId);
        } catch (err) {
            // setPointerCapture can throw if the pointer is already
            // released by the time we try to capture (test runners,
            // synthetic events). The gesture still works without
            // capture — we just lose the off-element guarantee. Capture
            // so the telemetry sink sees the breakage; the user-facing
            // path is unaffected.
            captureError(err, { scope: 'useSwipeGesture', op: 'setPointerCapture' });
        }
    }, []);

    const onPointerMove = useCallback((_e: ReactPointerEvent<HTMLElement>): void => {
        // No-op for now — we only fire on release. Kept in the returned
        // shape so future 1:1 drag tracking (per §5.5) can drop in
        // without changing the consumer API. Distance-based firing on
        // release matches the spec's distance threshold semantics.
    }, []);

    const finishGesture = useCallback(
        (e: ReactPointerEvent<HTMLElement>, cancelled: boolean): void => {
            const active = activeRef.current;
            if (active === null) return;
            if (active.id !== e.pointerId) return;

            activeRef.current = null;
            setIsSwiping(false);

            try {
                e.currentTarget.releasePointerCapture(e.pointerId);
            } catch (err) {
                // releasePointerCapture can throw if the capture was
                // already released (e.g. element unmounted mid-gesture).
                // The gesture is over either way; capture so unexpected
                // breakage shows up in telemetry without breaking the path.
                captureError(err, { scope: 'useSwipeGesture', op: 'releasePointerCapture' });
            }

            if (cancelled) return;

            const dx = e.clientX - active.startX;
            const dy = e.clientY - active.startY;
            const absX = Math.abs(dx);
            const absY = Math.abs(dy);

            // Below threshold → not a swipe. This is also how we
            // discriminate "tap" from "swipe" — a tap has near-zero
            // delta and never fires a direction callback.
            if (absX < threshold && absY < threshold) return;

            // Why: Dominant axis wins. Equal magnitudes prefer horizontal
            // (absX >= absY, not strict >) so the test matrix and the CI
            // swipe.detected telemetry payload are reproducible across
            // machines. Horizontal wins ties because the most common
            // gesture-conflict surface (the wizard step navigator) routes
            // exclusively on horizontal, so preferring horizontal minimises
            // false-axis dispatches on diagonal user input.
            // see useSwipeGesture.test.tsx for the pinned expectations
            // on each axis.
            let direction: SwipeDirection;
            if (absX >= absY) {
                direction = dx < 0 ? 'left' : 'right';
            } else {
                direction = dy < 0 ? 'up' : 'down';
            }

            const deltaPx = direction === 'left' || direction === 'right' ? absX : absY;

            // Telemetry first — fire regardless of whether the caller
            // wired a handler for this direction. Funnel analysis wants
            // to see ATTEMPTED swipes too (e.g. user swiped left on the
            // last step where onSwipeLeft is purposely left unwired).
            track('swipe.detected', { direction, deltaPx });

            switch (direction) {
                case 'left':
                    onSwipeLeft?.();
                    return;
                case 'right':
                    onSwipeRight?.();
                    return;
                case 'up':
                    onSwipeUp?.();
                    return;
                case 'down':
                    onSwipeDown?.();
                    return;
            }
        },
        [onSwipeLeft, onSwipeRight, onSwipeUp, onSwipeDown, threshold, track],
    );

    const onPointerUp = useCallback(
        (e: ReactPointerEvent<HTMLElement>): void => finishGesture(e, false),
        [finishGesture],
    );

    const onPointerCancel = useCallback(
        (e: ReactPointerEvent<HTMLElement>): void => finishGesture(e, true),
        [finishGesture],
    );

    const handlers = useMemo<SwipeHandlers>(
        () => ({
            onPointerDown,
            onPointerMove,
            onPointerUp,
            onPointerCancel,
        }),
        [onPointerDown, onPointerMove, onPointerUp, onPointerCancel],
    );

    if (isInactive) {
        return { handlers: NOOP_HANDLERS, isSwiping: false };
    }

    return { handlers, isSwiping };
}

export default useSwipeGesture;
